import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import Swal from 'sweetalert2';
import { formatNumber } from '../utils/numberFormatter';
import { formatDate } from '../utils/dateFormatter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faCamera, faImages } from '@fortawesome/free-solid-svg-icons';
import { usePageTitle } from '../hooks/usePageTitle';
import { REQUEST_STATUS_CONFIG, getStatusLabel, getStatusColors } from '../config/requestStatusConfig';
import { getCustomerRouteKey } from '../utils/customerRoute';

const RequestSkeleton = () => (
  <div className="col-12">
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div style={{ width: '60%' }}>
            <div className="skeleton-loader mb-2" style={{ width: '40%', height: '24px' }}></div>
            <div className="skeleton-loader" style={{ width: '80%', height: '18px' }}></div>
          </div>
          <div className="skeleton-loader" style={{ width: '20%', height: '24px' }}></div>
        </div>
        <div className="row mb-3">
          <div className="col-6">
            <div className="skeleton-loader mb-1" style={{ width: '30%', height: '14px' }}></div>
            <div className="skeleton-loader" style={{ width: '50%', height: '18px' }}></div>
          </div>
          <div className="col-6">
            <div className="skeleton-loader mb-1" style={{ width: '30%', height: '14px' }}></div>
            <div className="skeleton-loader" style={{ width: '50%', height: '18px' }}></div>
          </div>
        </div>
        <div className="skeleton-loader" style={{ width: '100%', height: '40px' }}></div>
      </div>
    </div>
  </div>
);

const MobileDocumentsPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [selectedRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [displayedRequests, setDisplayedRequests] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: 'pending_docs'
  });
  const [uploadProgress, setUploadProgress] = useState({});
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 25
  });
  const [request, setRequest] = useState(null);
  const [isWidgetLoading, setIsWidgetLoading] = useState(false);
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [isSavingCard, setIsSavingCard] = useState(false);
  const [cardErrorMessage, setCardErrorMessage] = useState('');
  const [isCardSaved, setIsCardSaved] = useState(false);
  const paymentWidgetInstanceRef = useRef(null);
  const paymentWidgetScriptRef = useRef(null);
  const widgetInitPromiseRef = useRef(null);
  const widgetOpenLockRef = useRef(false);

  // Obtenemos el scope del token
  const scope = useMemo(() => {
    const storedScope = localStorage.getItem('temp_tokenScope');
    return storedScope ? JSON.parse(storedScope) : {};
  }, []);

  // Obtenemos el tipo de token real del localStorage
  const tokenType = useMemo(() => {
    const storedTokenType = localStorage.getItem('temp_tokenType');
    
    // Mapeo de tipos de token a modo de operación
    const typeToMode = {
      'branch': 'seller-general',      // Sucursal: múltiples solicitudes
      'request': 'seller-request',          // Solicitud específica (vendedor)
      'customer': 'customer-request',    // Solicitud específica (cliente)
      'card_linking': 'customer-card-linking'
    };
    
    return typeToMode[storedTokenType] || 'seller-general';
  }, []);

  const rawTokenType = useMemo(() => localStorage.getItem('temp_tokenType'), []);
  const isCardLinkingMode = rawTokenType === 'card_linking';
  const customerRouteKey = getCustomerRouteKey(scope);
  const requestReference = scope.request_id;
  const requestPayload = useMemo(() => (
    scope.request_id
      ? { request_id: scope.request_id }
      : {}
  ), [scope.request_id]);
  const scriptSrc = process.env.REACT_APP_CIRENIO_PSP_SDK_SRC;
  const publicKey = process.env.REACT_APP_CIRENIO_PSP_PUBLIC_KEY;

  usePageTitle(isCardLinkingMode ? 'Vincular Tarjeta' : 'Documentos de Solicitudes');

  const initializeData = useCallback(async () => {
    try {
      const storedToken = localStorage.getItem('temp_authToken');
      const storedScope = localStorage.getItem('temp_tokenScope');

      if (storedToken && storedScope) {
        const scope = JSON.parse(storedScope);
        setTokenData({ scope });
        // fetchRequests se ejecutará automáticamente por el useEffect que observa filters
      } else {
        navigate(`/m/docs/${token}`);
      }
    } catch (error) {
      console.error('Error initializing data:', error);
      setError('Error al inicializar los datos');
      setLoading(false);
    }
  }, [navigate, token]);

  useEffect(() => {
    initializeData();
  }, [initializeData]);

  const fetchRequests = useCallback(async ({ page = 1, append = false } = {}) => {
    try {
      setLoadingMore(append);
      setLoading(!append);

      let response;
      
      if (tokenType === 'seller-general') {
        // Token general - traemos lista de solicitudes
        response = await api.get(`/requests?page=${page}&status=${filters.status}`);
        
        const requestsData = response.data.requests || [];
        setPagination(response.data.pagination);
        
        if (append) {
          setDisplayedRequests(prev => [...prev, ...requestsData]);
        } else {
          setDisplayedRequests(requestsData);
        }
      } else {
        // Token específico de solicitud (cliente o vendedor)
        response = await api.get(`/customers/${scope.customer_id}/requests/${scope.request_id}`);
        
        setDisplayedRequests([response.data]);
        setPagination({
          current_page: 1,
          last_page: 1,
          total: 1,
          per_page: 1
        });
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      setError('Error al cargar las solicitudes');
      setDisplayedRequests([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters.status, scope.customer_id, scope.request_id, tokenType]);

  const fetchRequestData = useCallback(async () => {
    try {
      if (!customerRouteKey || !requestReference) {
        setError('No se encontró el contexto necesario para vincular la tarjeta');
        setLoading(false);
        return;
      }

      setLoading(true);
      const response = await api.get(`/customers/${customerRouteKey}/requests/${requestReference}`);
      setRequest(response.data);
      setError(null);
    } catch (requestError) {
      console.error('Error fetching request request for card linking:', requestError);
      setError('No se pudo cargar la solicitud para vincular la tarjeta');
    } finally {
      setLoading(false);
    }
  }, [customerRouteKey, requestReference]);

  // Efecto para cargar solicitudes iniciales o la solicitud de vinculación
  useEffect(() => {
    if (!tokenData) return;

    if (isCardLinkingMode) {
      fetchRequestData();
      return;
    }

    fetchRequests();
  }, [fetchRequestData, fetchRequests, filters, isCardLinkingMode, tokenData]);

  const validateCardData = useCallback((cardData) => {
    if (!cardData?.source) {
      setCardErrorMessage('Datos de tarjeta incompletos. Por favor, intenta nuevamente.');
      return false;
    }

    return true;
  }, []);

  const destroyWidgetRuntime = useCallback(() => {
    if (paymentWidgetInstanceRef.current && typeof paymentWidgetInstanceRef.current.closeWidget === 'function') {
      paymentWidgetInstanceRef.current.closeWidget();
    }

    const currentScript = paymentWidgetScriptRef.current;
    if (currentScript && document.body.contains(currentScript)) {
      document.body.removeChild(currentScript);
    }

    const existingScript = scriptSrc
      ? document.querySelector(`script[src="${scriptSrc}"]`)
      : null;
    if (existingScript && document.body.contains(existingScript)) {
      document.body.removeChild(existingScript);
    }

    if (window.CirenioPayments) {
      delete window.CirenioPayments;
    }

    paymentWidgetInstanceRef.current = null;
    paymentWidgetScriptRef.current = null;
    widgetInitPromiseRef.current = null;
    widgetOpenLockRef.current = false;
    setIsWidgetOpen(false);
    setIsWidgetLoading(false);
  }, [scriptSrc]);

  const sendCardToApi = useCallback(async (payload) => {
    setIsSavingCard(true);

    try {
      const response = await api.post(`/customers/${customerRouteKey}/cards`, payload);

      if (response.status >= 200 && response.status < 300) {
        if (paymentWidgetInstanceRef.current && typeof paymentWidgetInstanceRef.current.closeWidget === 'function') {
          paymentWidgetInstanceRef.current.closeWidget();
        }

        setIsCardSaved(true);
        setCardErrorMessage('');
        setIsWidgetOpen(false);
        Swal.fire({
          title: 'Tarjeta vinculada',
          text: 'La tarjeta se vinculó correctamente. Vuelve con el vendedor para continuar.',
          icon: 'success',
          confirmButtonText: 'Entendido'
        });
        return true;
      }

      setCardErrorMessage('Ocurrió un problema al guardar la tarjeta. Intenta nuevamente.');
      return false;
    } catch (requestError) {
      console.error('Error storing card token from mobile card linking:', requestError);
      const backendMessage =
        requestError?.response?.data?.message ||
        requestError?.response?.data?.error ||
        null;
      setCardErrorMessage(backendMessage || 'No se pudo guardar la tarjeta. Intenta nuevamente.');
      widgetOpenLockRef.current = false;
      return false;
    } finally {
      setIsSavingCard(false);
    }
  }, [customerRouteKey]);

  const saveCardToken = useCallback(async (cardData) => {
    if (!cardData?.token || !cardData?.source) {
      setCardErrorMessage('El widget devolvió una respuesta incompleta. Intenta nuevamente.');
      widgetOpenLockRef.current = false;
      return;
    }

    if (!validateCardData(cardData)) return;

    const source = cardData.source;
    const payload = {
      token: cardData.token,
      holder: {
        id: source.holder?.identification,
        name: source.holder?.name
      },
      expiration: source.expiration,
      last_four: source.last_4,
      type: source.type,
      variant: source.variant,
      brand: {
        name: source.brand?.name,
        logo: source.brand?.logo_url
      },
      bank: {
        color: source.bank?.color || '',
        logo: source.bank?.logo_url || '',
        name: source.bank?.name || ''
      },
      ...requestPayload
    };

    await sendCardToApi(payload);
  }, [requestPayload, sendCardToApi, validateCardData]);

  const reloadWidget = useCallback(() => {
    destroyWidgetRuntime();
    setCardErrorMessage('');
    setIsCardSaved(false);
    setIsWidgetLoading(false);
    setIsWidgetOpen(false);
    widgetOpenLockRef.current = false;
  }, [destroyWidgetRuntime]);

  const initializeWidget = useCallback(async () => {
    if (paymentWidgetInstanceRef.current) {
      return paymentWidgetInstanceRef.current;
    }

    if (
      !isCardLinkingMode ||
      !request ||
      isCardSaved ||
      !customerRouteKey ||
      !requestReference ||
      !scriptSrc ||
      !publicKey
    ) {
      throw new Error('Widget de pagos sin contexto suficiente.');
    }

    if (widgetInitPromiseRef.current) {
      return widgetInitPromiseRef.current;
    }

    widgetInitPromiseRef.current = new Promise((resolve, reject) => {
      const createWidgetInstance = () => {
        if (!window.CirenioPayments) {
          widgetInitPromiseRef.current = null;
          reject(new Error('No se pudo inicializar el widget de pagos.'));
          return;
        }

        const payments = new window.CirenioPayments(
          publicKey,
          request.customer?.cuit || request.customer?.dni,
          {
            externalId: `mobile-card-${customerRouteKey}-${requestReference}`,
            firstName: request.customer?.name?.split(' ').slice(0, -1).join(' ') || request.customer?.name || '',
            lastName: request.customer?.name?.split(' ').slice(-1).join(' ') || '',
            validateIdentity: true,
            showCloseButton: true
          }
        );

        payments.on('widget_start', () => {
          setIsWidgetOpen(true);
          setIsWidgetLoading(false);
        });

        payments.on('tokenization_success', async (cardData) => {
          await saveCardToken(cardData);
        });

        payments.on('tokenization_failure', (payload) => {
          const widgetMessage = payload?.error || 'Hubo un problema al procesar tu tarjeta. Por favor, intenta nuevamente.';
          setIsWidgetOpen(false);
          setIsWidgetLoading(false);
          setCardErrorMessage(widgetMessage);
        });

        payments.on('widget_closed', () => {
          setIsWidgetOpen(false);
          setIsWidgetLoading(false);
          widgetOpenLockRef.current = false;
        });

        paymentWidgetInstanceRef.current = payments;
        widgetInitPromiseRef.current = null;
        resolve(payments);
      };

      const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
      if (existingScript && window.CirenioPayments) {
        paymentWidgetScriptRef.current = existingScript;
        createWidgetInstance();
        return;
      }

      const script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      paymentWidgetScriptRef.current = script;

      script.onload = createWidgetInstance;
      script.onerror = () => {
        widgetInitPromiseRef.current = null;
        setIsWidgetLoading(false);
        setCardErrorMessage('No se pudo cargar el widget de pagos. Revisa tu conexión o vuelve a intentarlo.');
        reject(new Error('No se pudo cargar el widget de pagos.'));
      };

      document.body.appendChild(script);
    });

    return widgetInitPromiseRef.current;
  }, [
    customerRouteKey,
    isCardLinkingMode,
    isCardSaved,
    publicKey,
    request,
    requestReference,
    saveCardToken,
    scriptSrc
  ]);

  const handleOpenWidget = useCallback(async () => {
    if (widgetOpenLockRef.current || isSavingCard || isCardSaved || isWidgetOpen) {
      return;
    }

    widgetOpenLockRef.current = true;
    setIsWidgetLoading(true);
    setCardErrorMessage('');

    try {
      const payments = await initializeWidget();

      if (!payments || typeof payments.openWidget !== 'function') {
        throw new Error('No se pudo abrir el widget de pagos.');
      }

      payments.openWidget();
    } catch (widgetError) {
      console.error('Error opening mobile card linking widget:', widgetError);
      setIsWidgetOpen(false);
      setIsWidgetLoading(false);
      widgetOpenLockRef.current = false;
      if (!cardErrorMessage) {
        setCardErrorMessage('No se pudo abrir el formulario seguro. Intenta nuevamente.');
      }
    }
  }, [cardErrorMessage, initializeWidget, isCardSaved, isSavingCard, isWidgetOpen]);

  useEffect(() => () => {
    destroyWidgetRuntime();
  }, [destroyWidgetRuntime]);

  const handleFileInput = async (request, documentType, documentId, file, documentSource = 'loan') => {
    try {
      setUploadProgress(prev => ({
        ...prev,
        [documentId]: 0
      }));

      const formData = new FormData();
      formData.append('files[]', file);
      formData.append('document_ids[]', documentId);
      formData.append('document_sources[]', documentSource);
      formData.append('categories[]', documentType);
      
      const response = await api.post(
        `/customers/${request.customer.id}/requests/${request.id}/documents`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(prev => ({
              ...prev,
              [documentId]: percentCompleted
            }));
          }
        }
      );
      
      // Limpiamos el progreso
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[documentId];
        return newProgress;
      });
      
      // Actualizamos la solicitud en el listado mostrado sin aplicar filtros
      setDisplayedRequests(prevRequests => 
        prevRequests.map(s => {
          if (s.id === request.id) {
            // Asegurarnos de que el status sea un string
            const newStatus = typeof response.data.request_status === 'string' 
              ? response.data.request_status 
              : (response.data.request_status?.value || response.data.request_status || s.status);
            return {
              ...s,
              status: newStatus,
              pending_documents: s.pending_documents.map(doc => {
                if (doc.id === documentId) {
                  return { ...doc, status: 'uploaded' };
                }
                return doc;
              })
            };
          }
          return s;
        })
      );
      
      const verifiedStatus = typeof response.data.request_status === 'string' 
        ? response.data.request_status 
        : (response.data.request_status?.value || response.data.request_status);
      if (verifiedStatus === 'verified') {
        Swal.fire({
          title: '¡Éxito!',
          text: 'Documentos subidos correctamente',
          icon: 'success'
        });
      }
      
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[documentId];
        return newProgress;
      });
      Swal.fire({
        title: 'Error',
        text: 'No se pudo subir el documento',
        icon: 'error'
      });
    }
  };

  const renderDocumentCard = (request, doc) => {
    // Verificamos el estado del documento directamente desde su propiedad status
    const isUploaded = doc.status === 'uploaded';
    const isUploading = uploadProgress[doc.id] !== undefined;
    const progress = uploadProgress[doc.id] || 0;

    return (
      <div key={doc.id} className="mob-document-upload-container mb-2">
        {isUploaded ? (
          <div className="mob-document-uploaded">
            <FontAwesomeIcon icon={faFileAlt} className="me-2" />
            {doc.title}
            <i className="bi bi-check-circle-fill text-success ms-2"></i>
          </div>
        ) : (
          <div className="mob-document-pending">
            <div className="mob-document-title">
              <FontAwesomeIcon icon={faFileAlt} className="me-2" />
              {doc.title}
            </div>
            {isUploading ? (
              <div className="upload-progress">
                <div className="progress">
                  <div 
                    className="progress-bar progress-bar-striped progress-bar-animated" 
                    role="progressbar" 
                    style={{ width: `${progress}%` }}
                    aria-valuenow={progress} 
                    aria-valuemin="0" 
                    aria-valuemax="100"
                  >
                    {progress}%
                  </div>
                </div>
                <small className="text-muted mt-1">Subiendo documento...</small>
              </div>
            ) : (
              <div className="mob-document-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => document.getElementById(`camera-${doc.id}`).click()}
                  title="Usar cámara"
                >
                  <FontAwesomeIcon icon={faCamera} className="me-2" />
                  Cámara
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={() => document.getElementById(`gallery-${doc.id}`).click()}
                  title="Elegir de galería"
                >
                  <FontAwesomeIcon icon={faImages} className="me-2" />
                  Galería
                </button>
              </div>
            )}
            <input
              id={`camera-${doc.id}`}
              type="file"
              accept="image/*"
              capture="environment"
              className="d-none"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  handleFileInput(request, doc.type, doc.id, e.target.files[0], doc.source || 'loan');
                }
              }}
            />
            <input
              id={`gallery-${doc.id}`}
              type="file"
              accept="image/*"
              className="d-none"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  handleFileInput(request, doc.type, doc.id, e.target.files[0], doc.source || 'loan');
                }
              }}
            />
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  if (isCardLinkingMode) {
    return (
      <div className="mobile-docs-container">
        <div className="container mt-4 mb-5">
          <div className="text-center mb-4">
            <p className="text-muted mb-0">
              Cargá tu tarjeta desde el celular para que el vendedor pueda seleccionarla y continuar con la solicitud.
            </p>
          </div>

          {request && (
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title mb-3">Titular de la solicitud</h5>
                <div className="row g-3">
                  <div className="col-12 col-sm-7">
                    <div>
                      <small className="text-muted d-block">Cliente</small>
                      <strong>{request.customer?.name}</strong>
                    </div>
                  </div>

                  <div className="col-12 col-sm-5">
                    <div className="text-sm-end">
                      <div>
                        <small className="text-muted d-block">DNI</small>
                        <strong>{request.customer?.dni}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title mb-3">Requisitos de la tarjeta</h5>
              <ul className="list-unstyled mb-0">
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Debe ser una tarjeta de débito.
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Debe estar a nombre del titular de la solicitud.
                </li>
                <li>
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Debe estar asociada a la cuenta donde recibes tus ingresos.
                </li>
              </ul>
            </div>
          </div>

          {isCardSaved ? (
            <div className="alert alert-success text-center">
              <i className="bi bi-check-circle-fill me-2"></i>
              Tu tarjeta ya quedó vinculada. Puedes volver con el vendedor para continuar.
            </div>
          ) : (
            <div className="card">
              <div className="card-body">
                <p className="text-muted mb-3">
                  Tocá el botón para cargar tu tarjeta.
                </p>

                <div className="d-grid gap-2">
                  <button
                    type="button"
                    className="btn btn-primary btn-lg"
                    onClick={handleOpenWidget}
                    disabled={isWidgetLoading || isSavingCard || isWidgetOpen}
                  >
                    <i className="bi bi-credit-card-2-front me-2" aria-hidden="true"></i>
                    {isWidgetLoading ? 'Abriendo formulario...' : 'Vincular Tarjeta'}
                  </button>

                  {(cardErrorMessage || isWidgetOpen) && !isCardSaved && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={reloadWidget}
                      disabled={isSavingCard}
                    >
                      Reiniciar formulario
                    </button>
                  )}
                </div>

                {cardErrorMessage && (
                  <div className="alert alert-warning text-center mt-3 mb-0" role="alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {cardErrorMessage}
                    {cardErrorMessage.includes('No se pudo cargar el widget de pagos') && (
                      <>
                        {' '}
                        <button type="button" className="btn btn-link p-0 align-baseline" onClick={reloadWidget}>
                          Reintentar
                        </button>
                      </>
                    )}
                  </div>
                )}

                {isSavingCard && (
                  <div className="text-center text-muted small mt-3">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Guardando tarjeta...
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (selectedRequest) {
    return (
      <div className="container mt-4">
        <h4>Solicitud #{selectedRequest.id}</h4>
        <p className="text-muted">Cliente: {selectedRequest.customer.name || selectedRequest.customer.dni}</p>
        
        <div className="documents-list mt-4">
          {selectedRequest.pending_documents && selectedRequest.pending_documents.length > 0 ? (
            selectedRequest.pending_documents.map(doc => renderDocumentCard(selectedRequest, doc))
          ) : (
            <div className="alert alert-success">
              <i className="bi bi-check-circle-fill me-2"></i>
              Todos los documentos han sido subidos correctamente.
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-docs-container">
      <div className="container mt-4">
        {/* Solo mostramos filtros para token general */}
        {tokenType === 'seller-general' && (
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-8">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar por DNI o número de solicitud..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  />
                </div>
                <div className="col-md-4">
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="">Todos los estados</option>
                    {Object.entries(REQUEST_STATUS_CONFIG).map(([value, config]) => (
                      <option key={value} value={value}>{config.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row g-4">
          {displayedRequests.map((request) => (
            <div key={request.id} className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h5 className="card-title mb-1">Solicitud #{request.id}</h5>
                    </div>
                    {(() => {
                      // Asegurarnos de que request.status sea un string
                      const status = typeof request.status === 'string' ? request.status : (request.status?.value || request.status || '');
                      const statusColors = getStatusColors(status);
                      const statusLabel = getStatusLabel(status);
                      return (
                        <span 
                          className="badge" 
                          style={{
                            backgroundColor: statusColors.backgroundColor,
                            color: statusColors.textColor
                          }}
                        >
                          {typeof statusLabel === 'string' ? statusLabel : (statusLabel?.label || String(status))}
                        </span>
                      );
                    })()}
                  </div>
                  
                  <div className='row mb-3'>
                    <div className='col-12'>
                      <p className='text-muted mb-0'>
                        Cliente: <b>{request.customer.name || request.customer.dni}</b><br />
                        DNI: <b>{request.customer.dni}</b>
                      </p>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-6">
                      <small className="text-muted d-block">Fecha</small>
                      <strong>{formatDate(request.request_date)}</strong>
                    </div>
                    <div className="col-6">
                      <small className="text-muted d-block">Monto</small>
                      <strong>${formatNumber(request.amount)}</strong>
                    </div>
                  </div>

                  {request.pending_documents && request.pending_documents.length > 0 && (
                    <div>
                      <h6 className="mb-3">
                        Documentos pendientes
                      </h6>
                      <div className="documents-grid">
                        {request.pending_documents.map(doc => renderDocumentCard(request, doc))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {loadingMore && (
            <>
              <RequestSkeleton />
              <RequestSkeleton />
              <RequestSkeleton />
            </>
          )}

          {/* Solo mostramos "cargar más" para token general */}
          {tokenType === 'seller-general' && 
           pagination.current_page < pagination.last_page && (
            <div className="col-12 text-center mt-4">
              <button 
                className="btn btn-outline-primary"
                onClick={() => fetchRequests({ page: pagination.current_page + 1, append: true })}
              >
                Cargar más solicitudes
              </button>
            </div>
          )}

          {!loading && !loadingMore && displayedRequests.length === 0 && (
            <div className="col-12">
              <div className="alert alert-info text-center">
                No se encontraron solicitudes que coincidan con los filtros aplicados.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileDocumentsPage; 